import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { caseStudies } from '@/data/mock';

const TimelineBlock = ({ label, title, items, summary }) => (
  <div className="py-12 first:pt-0 last:pb-0">
    <div className="flex items-center gap-4 mb-6">
      <span className="text-copper text-[12px] tracking-[0.2em] uppercase font-medium">{label}</span>
      <div className="flex-1 h-px bg-copper/20" />
    </div>
    <h3 className="font-display text-2xl text-vine-primary font-normal mb-6">{title}</h3>
    <div className="space-y-3 mb-4">
      {items.map((item, i) => (
        <p key={i} className="text-vine-primary/70 text-[15px] pl-5 border-l-2 border-copper/30">{item}</p>
      ))}
    </div>
    {summary && <p className="text-vine-primary/50 text-[14px] italic mt-4">{summary}</p>}
  </div>
);

export default function CaseStudyDetailPage() {
  const { slug } = useParams();
  const cs = caseStudies.find((c) => c.slug === slug);
  const currentIndex = caseStudies.findIndex((c) => c.slug === slug);
  const prevStudy = currentIndex > 0 ? caseStudies[currentIndex - 1] : null;
  const nextStudy = currentIndex < caseStudies.length - 1 ? caseStudies[currentIndex + 1] : null;

  if (!cs) {
    return (
      <div className="bg-vine-warm min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-3xl text-vine-primary">Case study not found.</h1>
          <Link to="/case-studies" className="mt-6 inline-flex items-center gap-2 text-copper text-[13px] tracking-[0.08em] uppercase">
            <ArrowLeft size={14} /> Back to case studies
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Hero */}
      <section className="bg-vine-deep text-vine-warm">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-36">
          <Link
            to="/case-studies"
            className="inline-flex items-center gap-2 text-vine-neutral/50 text-[13px] tracking-[0.08em] uppercase hover:text-copper transition-colors mb-10 block"
          >
            <ArrowLeft size={14} /> Case Studies
          </Link>
          <span className="text-copper text-[12px] tracking-[0.2em] font-medium block mb-4">
            Case {cs.number}
          </span>
          <h1 className="font-display text-4xl md:text-5xl font-light leading-[1.1] tracking-tight">
            {cs.title}
          </h1>
          <p className="mt-6 text-vine-neutral/50 text-[14px] tracking-[0.05em] uppercase">
            {cs.icp}
          </p>
        </div>
      </section>

      {/* Situation */}
      <section className="bg-vine-warm">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-24 md:py-32">
          <span className="section-label block mb-5">The Situation</span>
          <div className="space-y-4">
            {cs.situation.split('\n\n').map((para, i) => (
              <p key={i} className="text-vine-primary/70 text-[16px] leading-relaxed">{para}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="bg-vine-warm border-t border-vine-neutral/15">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-24 md:py-32">
          <span className="section-label block mb-10">Transformation Arc</span>
          <div className="divide-y divide-vine-neutral/15">
            <TimelineBlock label="Month 3" title={cs.month3.title} items={cs.month3.items} summary={cs.month3.summary} />
            <TimelineBlock label="Month 6" title={cs.month6.title} items={cs.month6.items} summary={cs.month6.summary} />
            <TimelineBlock label="Month 12" title={cs.month12.title} items={cs.month12.items} summary={cs.month12.summary} />
          </div>
        </div>
      </section>

      {/* Transformation */}
      <section className="bg-vine-deep text-vine-warm">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-20 md:py-28">
          <span className="section-label block mb-6">Transformation</span>
          <p className="font-display text-2xl md:text-3xl font-light leading-relaxed">
            {cs.transformation}
          </p>
        </div>
      </section>

      {/* Navigation */}
      <section className="bg-vine-warm border-t border-vine-neutral/15">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-12">
          <div className="flex justify-between items-center">
            {prevStudy ? (
              <Link
                to={`/case-studies/${prevStudy.slug}`}
                className="flex items-center gap-2 text-vine-primary/50 text-[13px] tracking-[0.05em] uppercase hover:text-copper transition-colors"
              >
                <ArrowLeft size={14} /> Previous
              </Link>
            ) : <div />}
            {nextStudy ? (
              <Link
                to={`/case-studies/${nextStudy.slug}`}
                className="flex items-center gap-2 text-vine-primary/50 text-[13px] tracking-[0.05em] uppercase hover:text-copper transition-colors"
              >
                Next <ArrowRight size={14} />
              </Link>
            ) : <div />}
          </div>
        </div>
      </section>
    </div>
  );
}
